import React, { useState, useEffect, useRef } from 'react';
import { Card, Input, Button, Space, message } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { getDefaultComponent, isSameFlowAction } from '../../utils/metaFlowUtils';
import ComponentRenderer from './ComponentRenderer';
import { useLanguage } from '../../contexts/LanguageContext';

const { TextArea } = Input;

const ScreenEditor = ({ screen, onUpdate, onComponentSelect, allScreens = [] }) => {
  const { t } = useLanguage();
  const [localScreen, setLocalScreen] = useState(screen);
  // 用於 Actions 內部組件的拖拽排序
  const dragIndexRef = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // 檢查並獲取 Body 顯示值（如果是默認值，返回翻譯後的默認值）
  const getBodyDisplayValue = () => {
    const bodyText = localScreen.data?.body?.text || '';
    const defaultBodyText = '請輸入內容';
    if (bodyText === defaultBodyText || bodyText === '') {
      // 如果是默認值或為空，顯示翻譯後的默認值
      return t('metaFlowBuilder.screenEditor.defaultValues.bodyText');
    }
    return bodyText;
  };
  
  // 檢查並獲取 Footer 顯示值（如果是默認值，返回翻譯後的默認值）
  const getFooterDisplayValue = () => {
    const footerText = localScreen.data?.footer?.text || '';
    const defaultFooterText = '提交';
    if (footerText === defaultFooterText || footerText === '') {
      // 如果是默認值或為空，顯示翻譯後的默認值
      return t('metaFlowBuilder.screenEditor.defaultValues.footerText');
    }
    return footerText;
  };
  
  // 檢查值是否為翻譯後的默認值
  const isTranslatedDefault = (value, defaultKey) => {
    const translatedDefault = t(defaultKey);
    return value === translatedDefault;
  };
  
  // 處理 Body 值變化
  const handleBodyChangeWithDefault = (value) => {
    // 如果用戶輸入的值等於翻譯後的默認值，或者為空，保存為硬編碼的默認值
    const translatedDefault = t('metaFlowBuilder.screenEditor.defaultValues.bodyText');
    if (!value || value.trim() === '' || value === translatedDefault) {
      handleBodyChange('請輸入內容');
    } else {
      handleBodyChange(value);
    }
  };
  
  // 處理 Footer 值變化
  const handleFooterChangeWithDefault = (value) => {
    // 如果用戶輸入的值等於翻譯後的默認值，或者為空，保存為硬編碼的默認值
    const translatedDefault = t('metaFlowBuilder.screenEditor.defaultValues.footerText');
    if (!value || value.trim() === '' || value === translatedDefault) {
      handleFooterChange('提交');
    } else {
      handleFooterChange(value);
    }
  };

  // 當 screen prop 改變時更新本地狀態
  useEffect(() => {
    setLocalScreen(screen);
  }, [screen]);

  // 更新本地狀態並通知父組件
  const updateScreen = (updates) => {
    const updated = { ...localScreen, ...updates };
    setLocalScreen(updated);
    console.log('📝 ScreenEditor updateScreen:', {
      screenId: updated.id,
      updates: updates,
      actionsCount: updated.data?.actions?.length || 0
    });
    onUpdate(updated);
  };

  // 更新 data 中的某個部分
  const updateData = (key, value) => {
    const newData = { ...localScreen.data, [key]: value };
    updateScreen({ data: newData });
  };

  // 處理拖放（從組件庫拖入新組件）
  const handleDrop = (e) => {
    e.preventDefault();
    const componentType = e.dataTransfer.getData('application/metaflow-component');
    if (componentType) {
      handleAddComponent(componentType);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // ── Actions 內部組件拖拽排序處理 ──
  const handleActionDragStart = (e, index) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/metaflow-action-reorder', String(index));
    // 透明拖影：使用拖拽元素本身作為預覽
  };

  const handleActionDragOver = (e, index) => {
    if (dragIndexRef.current === null) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleActionDragLeave = (e) => {
    e.stopPropagation();
  };

  const handleActionDrop = (e, dropIndex) => {
    if (dragIndexRef.current === null) return;
    e.preventDefault();
    e.stopPropagation();
    const fromIndex = dragIndexRef.current;
    dragIndexRef.current = null;
    setDragOverIndex(null);
    if (fromIndex === dropIndex) return;

    const currentActions = localScreen.data?.actions || [];
    if (fromIndex < 0 || fromIndex >= currentActions.length) return;
    if (dropIndex < 0 || dropIndex >= currentActions.length) return;

    const reordered = [...currentActions];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    updateData('actions', reordered);
  };

  const handleActionDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  // 添加組件
  const handleAddComponent = (componentType) => {
    const currentActions = localScreen.data?.actions || [];
    
    // 檢查 Image 組件數量限制（每個屏幕最多 3 張圖片）
    if (componentType === 'image') {
      const imageCount = currentActions.filter(action => action.type === 'image').length;
      if (imageCount >= 3) {
        message.error(t('metaFlowBuilder.screenEditor.messages.maxImagesError'));
        return;
      }
    }
    
    // 傳入現有組件列表，以便生成唯一的 name
    const newComponent = getDefaultComponent(componentType, null, currentActions);
    
    // 如果添加的是 RichText，需要移除 TextHeading 和 TextBody
    if (componentType === 'rich_text') {
      const updatedData = { ...localScreen.data };
      
      // 移除 Header（TextHeading）
      if (updatedData.header) {
        updatedData.header = null;
      }
      
      // 清空 Body（TextBody）
      if (updatedData.body) {
        updatedData.body = {
          type: 'body',
          text: ''
        };
      }
      
      // 更新 actions
      updatedData.actions = [...currentActions, newComponent];
      
      updateScreen({ data: updatedData });
      message.success(t('metaFlowBuilder.screenEditor.messages.addRichTextSuccess', { componentType }));
    } else {
      updateData('actions', [...currentActions, newComponent]);
      message.success(t('metaFlowBuilder.screenEditor.messages.addComponentSuccess', { componentType }));
    }
  };

  // 刪除組件
  const handleDeleteComponent = (componentRef) => {
    const currentActions = localScreen.data?.actions || [];
    const componentToDelete = currentActions.find(comp => 
      isSameFlowAction(comp, componentRef)
    );
    
    // 如果刪除的是 RichText，需要恢復 TextHeading 和 TextBody
    if (componentToDelete?.type === 'rich_text') {
      const updatedData = { ...localScreen.data };
      
      // 恢復 Header（TextHeading）- 如果之前沒有，設置默認值
      if (!updatedData.header) {
        updatedData.header = {
          type: 'header',
          format: 'TEXT',
          text: t('metaFlowBuilder.screenEditor.defaultValues.headerText')
        };
      }
      
      // 恢復 Body（TextBody）- 如果為空，設置默認值
      if (!updatedData.body || !updatedData.body.text) {
        updatedData.body = {
          type: 'body',
          text: t('metaFlowBuilder.screenEditor.defaultValues.bodyText')
        };
      }
      
      // 移除 RichText 組件
      updatedData.actions = currentActions.filter(comp => 
        !isSameFlowAction(comp, componentRef)
      );
      
      updateScreen({ data: updatedData });
      message.success(t('metaFlowBuilder.screenEditor.messages.deleteRichTextSuccess'));
    } else {
      updateData('actions', currentActions.filter(comp => 
        !isSameFlowAction(comp, componentRef)
      ));
      message.success(t('metaFlowBuilder.screenEditor.messages.deleteComponentSuccess'));
    }
  };

  // 更新組件
  const handleUpdateComponent = (componentRef, updates) => {
    const currentActions = localScreen.data?.actions || [];
    updateData('actions', currentActions.map(comp => 
      isSameFlowAction(comp, componentRef) ? { ...comp, ...updates } : comp
    ));
  };

  // 更新 Body（保留既有的 Template 文字 / 範例值）
  const handleBodyChange = (text) => {
    updateData('body', {
      ...(localScreen.data?.body || { type: 'body' }),
      type: 'body',
      text: text
    });
  };

  // 解析任意文字內 {{N}} placeholder，回傳由小到大排序的 N 陣列（去重）。
  const detectPlaceholders = (text) => {
    if (!text) return [];
    const indices = new Set();
    const re = /\{\{(\d+)\}\}/g;
    let m;
    while ((m = re.exec(text)) !== null) {
      const n = parseInt(m[1], 10);
      if (Number.isFinite(n) && n >= 1) indices.add(n);
    }
    return Array.from(indices).sort((a, b) => a - b);
  };

  // ── Template 專用欄位 handler ──
  // 只在第一屏顯示，因為 Template 只渲染 Flow 第一屏的 BODY/HEADER 文字。
  const handleTemplateBodyTextChange = (value) => {
    updateData('body', {
      ...(localScreen.data?.body || { type: 'body' }),
      type: 'body',
      text: localScreen.data?.body?.text || '',
      metaTemplateBodyText: value
    });
  };

  const handleTemplateHeaderTextChange = (value) => {
    updateData('header', {
      ...(localScreen.data?.header || { type: 'header', format: 'TEXT' }),
      type: 'header',
      format: 'TEXT',
      metaTemplateHeaderText: value
    });
  };

  const handleTemplateHeaderExampleChange = (value) => {
    updateData('header', {
      ...(localScreen.data?.header || { type: 'header', format: 'TEXT' }),
      type: 'header',
      format: 'TEXT',
      metaTemplateHeaderExample: value
    });
  };

  // 更新 Body 的某個 placeholder 範例值（[N-1] 對應 {{N}}）。
  // 範例值對應的是 Template Body 文字（metaTemplateBodyText）內的 {{N}}。
  const handleBodyExampleChange = (n, value) => {
    const examples = Array.isArray(localScreen.data?.body?.metaTemplateExamples)
      ? [...localScreen.data.body.metaTemplateExamples]
      : [];
    while (examples.length < n) examples.push('');
    examples[n - 1] = value;
    updateData('body', {
      ...(localScreen.data?.body || { type: 'body' }),
      type: 'body',
      text: localScreen.data?.body?.text || '',
      metaTemplateExamples: examples
    });
  };

  // 是否為 Flow 第一屏（Template 只渲染第一屏，Template 專用欄位只在此屏顯示，避免使用者誤填）。
  const isFirstScreen = (() => {
    if (!Array.isArray(allScreens) || allScreens.length === 0) return true;
    return allScreens[0]?.id === localScreen.id;
  })();

  // 更新 Footer
  const handleFooterChange = (text) => {
    // Footer 是必填項，如果為空則使用默認值（保存為中文 "提交"，顯示時會通過 getFooterDisplayValue 轉換）
    const footerText = text.trim() || '提交';
    updateData('footer', {
      type: 'footer',
      text: footerText
    });
  };

  // 更新 Header
  const handleHeaderChange = (field, value) => {
    const currentHeader = localScreen.data?.header || { type: 'header', format: 'TEXT' };
    updateData('header', {
      ...currentHeader,
      format: 'TEXT', // 固定為 TEXT 格式
      [field]: value
    });
  };

  return (
    <div 
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      style={{ minHeight: '100%' }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Header 編輯 */}
        {/* 如果存在 RichText 組件，隱藏 Header 和 Body 編輯器 */}
        {!localScreen.data?.actions?.some(comp => comp.type === 'rich_text') && (
        <Card size="small" title={t('metaFlowBuilder.screenEditor.cardTitles.header')}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {/* Flow 表單內 Header（FlowJson 內 TextHeading；Flow popup 顯示） */}
            <div>
              <label style={{ fontWeight: 600 }}>
                {t('metaFlowBuilder.screenEditor.labels.flowHeaderText')}
              </label>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2, marginBottom: 4 }}>
                {t('metaFlowBuilder.screenEditor.helperText.flowHeaderHint')}
              </div>
              <Input
                value={localScreen.data?.header?.text || ''}
                onChange={(e) => handleHeaderChange('text', e.target.value)}
                placeholder={t('metaFlowBuilder.screenEditor.placeholders.headerText')}
              />
            </div>

            {/* WhatsApp 訊息 Header（Template HEADER；只第一屏渲染進 Template，故僅於第一屏顯示） */}
            {isFirstScreen && (
              <div style={{ padding: 12, background: '#f0f7ff', border: '1px dashed #91caff', borderRadius: 4 }}>
                <label style={{ fontWeight: 600 }}>
                  {t('metaFlowBuilder.screenEditor.labels.templateHeaderText')}
                </label>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2, marginBottom: 4 }}>
                  {t('metaFlowBuilder.screenEditor.helperText.templateHeaderHint')}
                </div>
                <Input
                  value={localScreen.data?.header?.metaTemplateHeaderText || ''}
                  onChange={(e) => handleTemplateHeaderTextChange(e.target.value)}
                  placeholder={t('metaFlowBuilder.screenEditor.placeholders.templateHeaderText')}
                  maxLength={60}
                />
                {/* HEADER 偵測到 {{1}} 時顯示單一範例值欄（Meta API 限制 HEADER 只支援 {{1}}） */}
                {(() => {
                  const headerText = localScreen.data?.header?.metaTemplateHeaderText || '';
                  const indices = detectPlaceholders(headerText);
                  if (indices.length === 0) return null;
                  if (indices.length > 1) {
                    return (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#fa8c16' }}>
                        {t('metaFlowBuilder.screenEditor.helperText.templateHeaderTooManyPlaceholders')}
                      </div>
                    );
                  }
                  return (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{
                        flexShrink: 0,
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 4,
                        padding: '2px 8px',
                        fontFamily: 'monospace',
                        color: '#1677ff',
                        minWidth: 56,
                        textAlign: 'center'
                      }}>{`{{1}}`}</code>
                      <Input
                        value={localScreen.data?.header?.metaTemplateHeaderExample || ''}
                        onChange={(e) => handleTemplateHeaderExampleChange(e.target.value)}
                        placeholder={t('metaFlowBuilder.screenEditor.placeholders.bodyExampleValue', { n: 1 })}
                        maxLength={60}
                      />
                    </div>
                  );
                })()}
              </div>
            )}

            {localScreen.data?.header && (
              <Button
                size="small"
                danger
                onClick={() => updateData('header', null)}
              >
                {t('metaFlowBuilder.screenEditor.buttons.removeHeader')}
              </Button>
            )}
            {!localScreen.data?.header && (
              <Button
                size="small"
                onClick={() => {
                  updateData('header', {
                    type: 'header',
                    format: 'TEXT',
                    text: ''
                  });
                }}
              >
                {t('metaFlowBuilder.screenEditor.buttons.addHeader')}
              </Button>
            )}
          </Space>
        </Card>
        )}

        {/* Body 編輯 */}
        {!localScreen.data?.actions?.some(comp => comp.type === 'rich_text') && (
        <Card size="small" title={t('metaFlowBuilder.screenEditor.cardTitles.body')}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {/* Flow 表單內 Body（FlowJson 內 TextBody；Flow popup 顯示，{{N}} 不會被替換，建議寫固定話術） */}
            <div>
              <label style={{ fontWeight: 600 }}>
                {t('metaFlowBuilder.screenEditor.labels.flowBodyText')}
              </label>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2, marginBottom: 4 }}>
                {t('metaFlowBuilder.screenEditor.helperText.flowBodyHint')}
              </div>
              <TextArea
                value={getBodyDisplayValue()}
                onChange={(e) => handleBodyChangeWithDefault(e.target.value)}
                placeholder={t('metaFlowBuilder.screenEditor.placeholders.bodyContent')}
                rows={4}
              />
            </div>

            {/* WhatsApp 訊息 Body（Template BODY；只第一屏渲染進 Template，故僅於第一屏顯示） */}
            {isFirstScreen && (
              <div style={{ padding: 12, background: '#f0f7ff', border: '1px dashed #91caff', borderRadius: 4 }}>
                <label style={{ fontWeight: 600 }}>
                  {t('metaFlowBuilder.screenEditor.labels.templateBodyText')}
                </label>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2, marginBottom: 4 }}>
                  {t('metaFlowBuilder.screenEditor.helperText.templateBodyHint')}
                </div>
                <TextArea
                  value={localScreen.data?.body?.metaTemplateBodyText || ''}
                  onChange={(e) => handleTemplateBodyTextChange(e.target.value)}
                  placeholder={t('metaFlowBuilder.screenEditor.placeholders.templateBodyText')}
                  rows={4}
                />
                {(() => {
                  // 若 Template Body 含 {{N}} placeholder，顯示「範例值」輸入欄（送 Meta 審核用）。
                  const indices = detectPlaceholders(localScreen.data?.body?.metaTemplateBodyText);
                  if (indices.length === 0) return null;
                  const examples = Array.isArray(localScreen.data?.body?.metaTemplateExamples)
                    ? localScreen.data.body.metaTemplateExamples
                    : [];
                  return (
                    <div style={{ marginTop: 12, padding: 12, background: '#fff', border: '1px dashed #d9d9d9', borderRadius: 4 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        {t('metaFlowBuilder.screenEditor.labels.bodyExamplesTitle')}
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                        {t('metaFlowBuilder.screenEditor.helperText.bodyExamplesHint')}
                      </div>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {indices.map((n) => (
                          <div key={`body-ex-${n}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <code style={{
                              flexShrink: 0,
                              background: '#fff',
                              border: '1px solid #e0e0e0',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontFamily: 'monospace',
                              color: '#1677ff',
                              minWidth: 56,
                              textAlign: 'center'
                            }}>{`{{${n}}}`}</code>
                            <Input
                              value={examples[n - 1] || ''}
                              onChange={(e) => handleBodyExampleChange(n, e.target.value)}
                              placeholder={t('metaFlowBuilder.screenEditor.placeholders.bodyExampleValue', { n })}
                              maxLength={60}
                            />
                          </div>
                        ))}
                      </Space>
                    </div>
                  );
                })()}
              </div>
            )}
          </Space>
        </Card>
        )}

        {/* Footer 編輯 */}
        <Card size="small" title={t('metaFlowBuilder.screenEditor.cardTitles.footer')}>
          <Input
            value={getFooterDisplayValue()}
            onChange={(e) => handleFooterChangeWithDefault(e.target.value)}
            placeholder={t('metaFlowBuilder.screenEditor.placeholders.footerContent')}
            style={{ marginTop: '4px' }}
            maxLength={60}
            showCount
            required
          />
          <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '4px' }}>
            {t('metaFlowBuilder.screenEditor.helperText.footerRequired')}
          </div>
        </Card>

        {/* Actions 編輯 */}
        <Card 
          size="small" 
          title={t('metaFlowBuilder.screenEditor.cardTitles.actions')}
        >
          <div style={{ 
            minHeight: '200px',
            padding: '12px',
            border: '2px dashed #d9d9d9',
            borderRadius: '4px',
            backgroundColor: '#fafafa'
          }}>
                {localScreen.data?.actions && localScreen.data.actions.length > 0 ? (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {localScreen.data.actions.map((component, index) => {
                  const isDragOver = dragOverIndex === index && dragIndexRef.current !== null && dragIndexRef.current !== index;
                  return (
                    <div
                      key={component.id || component.name || `component_${index}`}
                      onDragOver={(e) => handleActionDragOver(e, index)}
                      onDragLeave={handleActionDragLeave}
                      onDrop={(e) => handleActionDrop(e, index)}
                      style={{
                        position: 'relative',
                        borderTop: isDragOver && (dragIndexRef.current ?? -1) > index ? '2px solid #1677ff' : '2px solid transparent',
                        borderBottom: isDragOver && (dragIndexRef.current ?? -1) < index ? '2px solid #1677ff' : '2px solid transparent',
                        transition: 'border 0.15s ease'
                      }}
                    >
                      <div
                        onClick={(e) => {
                          if (e.target.closest('button')) return;
                          if (e.target.closest('[data-drag-handle]')) return;
                          if (onComponentSelect) onComponentSelect(component);
                        }}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'stretch', gap: 8 }}
                      >
                        <div
                          data-drag-handle="true"
                          draggable
                          onDragStart={(e) => handleActionDragStart(e, index)}
                          onDragEnd={handleActionDragEnd}
                          title={t('metaFlowBuilder.screenEditor.tooltips.dragToReorder') || '拖拽以調整順序'}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            cursor: 'grab',
                            color: '#999',
                            userSelect: 'none',
                            flexShrink: 0
                          }}
                          onMouseDown={(e) => { e.currentTarget.style.cursor = 'grabbing'; }}
                          onMouseUp={(e) => { e.currentTarget.style.cursor = 'grab'; }}
                        >
                          <HolderOutlined />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <ComponentRenderer
                            component={component}
                            onUpdate={(updates) => handleUpdateComponent(component, updates)}
                            onDelete={(e) => {
                              e?.stopPropagation();
                              handleDeleteComponent(component);
                            }}
                            onEdit={(comp) => {
                              if (onComponentSelect) onComponentSelect(comp);
                            }}
                            screenId={localScreen.id}
                            allScreens={allScreens}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Space>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                color: '#999', 
                padding: '40px 0',
                fontSize: '14px'
              }}>
                {t('metaFlowBuilder.screenEditor.placeholders.dropZone')}
              </div>
            )}
          </div>
        </Card>
      </Space>
    </div>
  );
};

export default ScreenEditor;

